import { Button, Form, Input, Modal, Spin } from 'antd';
import dynamic from 'next/dynamic';
import { array, bool, func, object, string } from 'prop-types';
import React, { useEffect, useRef, useState } from 'react';
import { DialogTrigger } from 'react-aria-components';
import { tv } from 'tailwind-variants';

import { focusRing } from 'components/ui/component-utils';
import { Button as ButtonLab } from 'components/ui/button';
import { Drawer as DrawerLab, DrawerRoot, DrawerX } from 'components/ui/drawer';
import { Select, SelectItem } from 'components/ui/select';
import { Button as LabButton } from 'components/ui/button';
import { LabCloseIcon } from 'components/camperlab/icons/close';
import { LabChevronDownNotify } from 'components/camperlab/icons/chevron-down';
import { getProductsRecommended, notifyMe } from 'actions/products';
import FormInput from 'components/formInput';
import { CloseModalSVG } from 'components/iconsSVG/close';
import ProtectionDataModal from 'components/protectionDataModal';
import PrivacyPolicy from 'components/privacyPolicy';
import { ThankYouMessage } from 'components/thankYouMessage';
import { useI18n } from 'context/i18n';
import { IS_CAMPER, IS_NNORMAL } from 'utils/constants/system';
import { getMarket } from 'utils/helpers';

import { useIsMounted, useValidate } from 'hooks';
import { isBlank } from 'utils/StringUtils';

import styles from './style.module.css';
import { getDomainValue } from 'utils/domain';

const NnormalSVG = dynamic(() => import('components/iconsSVG/nnormal').then((mod) => mod.NnormalSVG));
const NotifySVG = dynamic(() => import('components/iconsSVG/notify').then((mod) => mod.NotifySVG));
const SizesList = dynamic(() => import('components/sizesList'));
const ProductsRows = dynamic(() => import('components/ProductsRow'));

export function NotifyMeModal({ code, isModalVisible, handleCancel, productSheet, defaultSize, camperlab = false }) {
  const { t, locale } = useI18n();
  const validate = useValidate();
  const isMounted = useIsMounted();
  const [loading, setLoading] = useState(false);
  const [notifyForm] = Form.useForm();
  const [finished, setFinished] = useState(false);
  const [selectedSize, setSelectedSize] = useState(defaultSize);
  const loadingChanged = useRef(false);
  const [recommended, setRecommended] = useState([]);
  const hasFetchedRecommended = useRef(false);

  const sizesAvailable = productSheet.sizes
    .filter((s) => !isBlank(s.value))
    .map((s) => ({
      ...s,
      available: productSheet.notifyMe && productSheet.notifyMeType !== 'SIZE' ? true : !s.available,
    }))
    .filter((s) => s.available);

  const onFinish = async (values) => {
    setLoading(true);
    try {
      if (values?.privacy === true) {
        await notifyMe({
          profile: locale,
          name: values.name,
          email: values.email,
          size: selectedSize,
          productId: code,
          newsletter: true,
          notifymeForSize: productSheet?.notifyMeType === 'SIZE',
          lab: getDomainValue({ camper: camperlab === true, camperlab: true, nnormal: false }),
        });
      }
      setLoading(false);
    } catch (error) {
      console.log(error);
      setLoading(false);
    }
  };

  useEffect(() => {
    if (loadingChanged.current === true && loading === false) {
      setFinished(true);
    } else {
      loadingChanged.current = true;
    }
  }, [loading]);

  useEffect(() => {
    setSelectedSize(defaultSize);
  }, [defaultSize]);

  useEffect(() => {
    if (!hasFetchedRecommended.current) {
      hasFetchedRecommended.current = true;
      const fetchRecommended = async () => {
        try {
          const response = await getProductsRecommended({
            lang: locale,
            productCode: productSheet.code,
            ia: 'true',
            target: IS_NNORMAL ? productSheet.target : undefined,
            forModal: true,
            targetIdUrl: productSheet.realTarget,
          });
          if (isMounted()) {
            setRecommended(() =>
              response.recommendeds.map((p) => {
                delete p.colors;
                delete p.subtitleH1;
                delete p.sizes;
                return p;
              }),
            );
          }
        } catch (e) {
          console.error('Fallo la llamada de productos recomendados', e);
        }
      };
      fetchRecommended();
    }
  }, [isModalVisible]);

  const notifyMeType = productSheet.notifyMeType !== null ? `.${productSheet.notifyMeType}` : '';
  const specialTxt = productSheet.notifyMeSpecialLabel ? '.special' : '';
  const market = getMarket(locale);

  return (
    <Modal
      title=""
      visible={isModalVisible}
      footer={null}
      onCancel={handleCancel}
      wrapClassName={`${styles.modalWrapper} ${finished === true ? styles.finished : ''} ${Array.isArray(recommended) && recommended.length > 0 ? styles.withRecommendations : ''}`}
      closeIcon={<CloseModalSVG />}
    >
      <div className={styles.modalContent}>
        {finished === false ?
          <>
            {IS_CAMPER ?
              <NotifySVG />
            : <NnormalSVG />}
            <p>{t('ficha.producto', 'modal.members.only.title', 'Members Only')}</p>
            <p className={styles.title}>{t('ficha.producto', 'notify.titulo', 'Not available product')}</p>
            <p className={styles.subtitle}>{t('ficha.producto', `notify.subtitulo${notifyMeType}${specialTxt}`, 'En estos momentos...')}</p>
            {sizesAvailable?.length > 0 && (
              <div className={styles.sizesDiv}>
                <p className={styles.sizesTitle}>{t('ficha.producto', 'notify.selectsize', 'Choose one size to check in-store availability.')}</p>
                <div className={styles.sizeListWrapper}>
                  <SizesList
                    sizes={sizesAvailable}
                    defaultSelectedSize={selectedSize}
                    onChange={setSelectedSize}
                    select={IS_NNORMAL}
                    productSheet={productSheet}
                    currentCountry={market}
                  />
                </div>
              </div>
            )}

            <p className={styles.subtitle}>{t('ficha.producto', 'notify.desc', 'Sin embargo, nuestro objetivo es garantizar...')}</p>

            <Form
              form={notifyForm}
              layout="vertical"
              name="notifyMe"
              onFinish={onFinish}
              requiredMark="optional"
              scrollToFirstError
              className={styles.customForm}
            >
              <FormInput name="name" rules={validate('name')} floatLabel="" required>
                <Input placeholder="Name" />
              </FormInput>
              <FormInput name="email" rules={validate('email')} required>
                <Input placeholder="Email" type="email" formNoValidate />
              </FormInput>

              <PrivacyPolicy hideConditions wrapperClass={styles.noBottomMargin} checkBoxClass={styles.customCheckbox} />

              <Spin spinning={loading}>
                <Form.Item>
                  <Button type="default" htmlType="submit" className={styles.submitBtn}>
                    {t('modal.idiomas', 'submit')}
                  </Button>
                </Form.Item>
              </Spin>
            </Form>
          </>
        : <>
            <ThankYouMessage type="notifyme" />
            {Array.isArray(recommended) && recommended.length > 0 ?
              <>
                <p className={styles.recommendedTitle}>{t('ficha.producto', 'tambien.puede.interesarte')}</p>
                <ProductsRows
                  locale={locale}
                  source={recommended}
                  currentList="You_may_also_be_interested"
                  slider={true}
                  camperlab={productSheet.camperlab}
                  withHover={false}
                  fixedGrid={false}
                  forcedSettings={{
                    dots: false,
                    infinite: false,
                    speed: 500,
                    slidesToShow: 4.33,
                    initialSlide: 0,
                    swipeToSlide: true,
                    easing: false,
                    draggable: true,
                    arrows: true,
                    swipe: true,
                    useTransform: false,
                    touchThreshold: 50,
                    responsive: [
                      {
                        breakpoint: 1440,
                        settings: {
                          arrows: false,
                          slidesToShow: 4.6,
                          slidesToScroll: 1,
                        },
                      },
                      {
                        breakpoint: 448,
                        settings: {
                          arrows: false,
                          slidesToShow: 2.2,
                          slidesToScroll: 1,
                        },
                      },
                    ],
                  }}
                />
              </>
            : null}
          </>
        }
      </div>
    </Modal>
  );
}

NotifyMeModal.propTypes = {
  isModalVisible: bool,
  handleCancel: func,
  code: string,
  productSheet: object,
  defaultSize: string,
};

function NotifyMeDrawer({ code, isModalVisible, handleCancel, productSheet, defaultSize, camperlab = false }) {
  const { t, locale, profileData } = useI18n();
  const validate = useValidate();
  const isMounted = useIsMounted();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    size: '',
    privacy: false,
  });
  const [privacyPolicyModalVisible, setPrivacyPolicyModalVisibile] = useState(false);
  const [finished, setFinished] = useState(false);
  const [selectedSize, setSelectedSize] = useState(defaultSize);
  const loadingChanged = useRef(false);
  const [recommended, setRecommended] = useState([]);
  const hasFetchedRecommended = useRef(false);

  const sizesAvailable = productSheet.sizes
    .filter((s) => !isBlank(s.value))
    .map((s) => ({
      ...s,
      available: productSheet.notifyMe && productSheet.notifyMeType !== 'SIZE' ? true : !s.available,
    }))
    .filter((s) => s.available);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value,
    });
  };

  const onFinish = async (e) => {
    e.preventDefault();
    setLoading(true);
    const values = formData;
    try {
      if (values?.privacy === true) {
        await notifyMe({
          profile: locale,
          name: values.name,
          email: values.email,
          size: selectedSize,
          productId: code,
          newsletter: true,
          notifymeForSize: productSheet?.notifyMeType === 'SIZE',
          lab: true,
        });
      }
      setLoading(false);
    } catch (error) {
      console.log(error);
      setLoading(false);
      setError(String(error));
    }
  };

  useEffect(() => {
    if (loadingChanged.current === true && loading === false) {
      setFinished(true);
    } else {
      loadingChanged.current = true;
    }
  }, [loading]);

  useEffect(() => {
    setSelectedSize(defaultSize);
  }, [defaultSize]);

  useEffect(() => {
    if (!hasFetchedRecommended.current) {
      hasFetchedRecommended.current = true;
      const fetchRecommended = async () => {
        try {
          const response = await getProductsRecommended({
            lang: locale,
            productCode: productSheet.code,
            ia: 'true',
            target: IS_NNORMAL ? productSheet.target : undefined,
            forModal: true,
            targetIdUrl: productSheet.realTarget,
          });
          if (isMounted()) {
            setRecommended(() =>
              response.recommendeds.map((p) => {
                delete p.colors;
                delete p.subtitleH1;
                delete p.sizes;
                return p;
              }),
            );
          }
        } catch (e) {
          console.error('Fallo la llamada de productos recomendados', e);
        }
      };
      fetchRecommended();
    }
  }, [isModalVisible]);

  const notifyMeType = productSheet.notifyMeType !== null ? `.${productSheet.notifyMeType}` : '';
  const specialTxt = productSheet.notifyMeSpecialLabel ? '.special' : '';
  const market = getMarket(locale);
  const fontSizeClass = '!text-[14px] !leading-[16.1px] md:!text-[12px] md:!leading-[14.4px]';
  const inputClass = 'mb-4 !text-[16px] !leading-[18.4px] md:!text-[12px] md:!leading-[14.4px]';
  const inputCheckboxClass = 'flex items-baseline !text-[16px] !leading-[18.4px] md:!text-[12px] md:!leading-[14.4px]';
  const styles = tv({
    extend: focusRing,
    base: 'bg-clear flex h-[30px] w-full min-w-[150px] cursor-default items-center gap-4 border border-[#CCCCCC] px-1.5 text-start transition md:h-6',
    variants: {
      isDisabled: {
        false: 'text-copy group-invalid:border-error forced-colors:group-invalid:border-[Mark]',
        true: 'text-disabled dark:border-disabled forced-colors:border-[GrayText] forced-colors:text-[GrayText]',
      },
    },
  });
  return (
    <DialogTrigger isOpen={isModalVisible} onOpenChange={(isOpen) => (!isOpen ? handleCancel() : () => true)}>
      <DrawerRoot className="w-full md:max-w-sm lg:max-w-sm">
        <DrawerLab>
          <div className="relative flex h-full max-h-screen flex-grow flex-col overflow-x-hidden">
            <div className="bg-clear sticky top-0 mb-32 flex flex-shrink-0 items-center justify-between p-2.5">
              <div className="flex items-center md:relative md:order-2">
                <DrawerX absolute={false} />
              </div>
              <span className="hidden text-sm font-bold uppercase md:order-1 md:block">{t('ficha.producto', `avisame${notifyMeType}`, 'NOTIFICARME')}</span>
            </div>
            <form onSubmit={onFinish} className="flex w-full flex-grow flex-col">
              <div className="flex-grow px-2.5">
                <div className="flex flex-shrink-0 flex-col">
                  <p className={`mb-1 font-bold uppercase ${fontSizeClass}`}>{t('ficha.producto', 'notify.titulo')}</p>
                  <p className={`mb-5 ${fontSizeClass}`}>{t('ficha.producto', `notify.subtitulo${notifyMeType}${specialTxt}`)}</p>
                  <div className={inputClass}>
                    <label htmlFor="name" className="mb-1 block font-bold">
                      {t('ficha.producto', 'notify.name', 'Name')}
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      className="h-[30px] w-full border border-[#CCCCCC] px-1 focus:outline-none focus:ring-2 md:h-6"
                      placeholder=""
                    />
                  </div>
                  <div className={inputClass}>
                    <label htmlFor="email" className="mb-1 block font-bold">
                      {t('ficha.producto', 'notify.email', 'Email')}
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="h-[30px] w-full border border-[#CCCCCC] px-1 focus:outline-none focus:ring-2 md:h-6"
                      placeholder=""
                    />
                  </div>
                  <div className={inputClass}>
                    <label htmlFor="size" className="mb-1 block font-bold">
                      {t('ficha.producto', 'notify.select.size', 'What size are you looking for?')}
                    </label>
                    <Select
                      label={t('ficha.producto', 'elije.tu.talla')}
                      hiddenLabel
                      items={productSheet.sizes}
                      selectedKey={selectedSize}
                      onSelectionChange={setSelectedSize}
                      icon={<LabChevronDownNotify />}
                      className={'h-[30px] md:h-6'}
                      required={true}
                      tvStyles={styles}
                    >
                      {(item) => (
                        <SelectItem key={item.value} id={item.value}>
                          {item.label}
                        </SelectItem>
                      )}
                    </Select>
                  </div>
                  <button type="button" className="mb-5 mt-1 text-left text-xs font-bold underline" onClick={() => setPrivacyPolicyModalVisibile(true)}>
                    {t('generico', 'camper.newsletter.proteccion.datos')}
                  </button>
                  <ProtectionDataModal visible={privacyPolicyModalVisible} onCancel={() => setPrivacyPolicyModalVisibile(false)} origin="registro.myaccount" />
                  <div className={inputCheckboxClass}>
                    <input
                      type="checkbox"
                      id="privacy"
                      name="privacy"
                      checked={formData.privacy}
                      required
                      onChange={handleChange}
                      className="text-black border-black h-3 w-3 rounded-none border accent-copy md:h-[9px] md:w-[9px]"
                    />
                    <label htmlFor="privacy" className="ml-2 font-bold">
                      {t('compra.formulario', 'acepto.las')}&nbsp;
                      <a href={profileData.urlPrivacyPolicy} target="_blank" rel="noreferrer">
                        {t('generico', 'privacy.conditions', 'privacy conditions')}
                      </a>
                    </label>
                  </div>
                  {error !== false ?
                    <p className="text-error">{error}</p>
                  : null}
                </div>
              </div>
              <div className="bg-clear sticky bottom-0 flex flex-shrink-0 flex-col gap-2.5 p-2.5">
                <ButtonLab type="submit" isPending={loading} disabled={loading} className="h-8 w-full px-2 text-sm font-bold uppercase md:h-6">
                  <span className="text-sm md:text-xs">{t('ficha.producto', 'notify.confirm', 'Confirm')}</span>
                </ButtonLab>
              </div>
            </form>
          </div>
        </DrawerLab>
      </DrawerRoot>
    </DialogTrigger>
  );
}

NotifyMeDrawer.propTypes = {
  isModalVisible: bool,
  handleCancel: func,
  code: string,
  productSheet: object,
  defaultSize: string,
};

function NotifyMeButton({ code, productSheet, defaultSize, camperlab }) {
  const { t } = useI18n();

  const [isModalVisible, setIsModalVisible] = useState(false);

  const showModal = () => {
    setIsModalVisible(true);
  };
  const handleCancel = () => {
    setIsModalVisible(false);
  };

  const notifyMeType = productSheet.notifyMeType !== null ? `.${productSheet.notifyMeType}` : '';

  return (
    <>
      {getDomainValue({
        camper: (
          <Button block size="large" type="primary" className={styles.notifyBtn} onClick={showModal}>
            {t('ficha.producto', `avisame${notifyMeType}`, 'NOTIFICARME')}
          </Button>
        ),
        nnormal: (
          <Button block size="large" type="primary" className={styles.notifyBtn} onClick={showModal}>
            {t('ficha.producto', `avisame${notifyMeType}`, 'NOTIFICARME')}
          </Button>
        ),
        camperlab: <LabButton onPress={showModal}>{t('ficha.producto', `avisame${notifyMeType}`, 'NOTIFICARME')}</LabButton>,
      })}
      {getDomainValue({
        camper: (
          <NotifyMeModal
            code={code}
            handleCancel={handleCancel}
            isModalVisible={isModalVisible}
            productSheet={productSheet}
            defaultSize={defaultSize}
            camperlab={camperlab}
          />
        ),
        nnormal: (
          <NotifyMeModal
            code={code}
            handleCancel={handleCancel}
            isModalVisible={isModalVisible}
            productSheet={productSheet}
            defaultSize={defaultSize}
            camperlab={camperlab}
          />
        ),
        camperlab: (
          <NotifyMeDrawer
            code={code}
            handleCancel={handleCancel}
            isModalVisible={isModalVisible}
            productSheet={productSheet}
            defaultSize={defaultSize}
            camperlab={camperlab}
          />
        ),
      })}
    </>
  );
}

NotifyMeButton.propTypes = {
  code: string,
  productSheet: object,
  defaultSize: string,
  recommendedProducts: array,
};

export default NotifyMeButton;
